import {useState, useCallback, useMemo} from "react";
import {
	GridFilterModel,
	GridSortModel,
	GridInitialState
} from "@mui/x-data-grid-pro";
import {
	PlaylistEnrollmentDto,
	useListPlaylistEnrollments,
	PlaylistsControllerListEnrollmentsParams
} from "@/app2/shared/data";

interface UsePlaylistEnrollmentsGridProps {
	playlistId: number;
	cohortId: number;
	selected: number[];
	setSelected: (ids: number[]) => void;
}

export const usePlaylistEnrollmentsGrid = ({
	playlistId,
	cohortId,
	selected,
	setSelected
}: UsePlaylistEnrollmentsGridProps) => {
	const [filterModel, setFilterModel] = useState<PlaylistsControllerListEnrollmentsParams>({
		page: 1, // 1-based pagination
		pageSize: 25
	});

	const {data, isFetching, refetch} = useListPlaylistEnrollments(playlistId, cohortId, filterModel);

	// Ensure we always have valid defaults
	const enrollments: PlaylistEnrollmentDto[] = data?.data ?? [];
	const rowCount = data?.total ?? 0;

	const onPageChange = useCallback((page: number) => {
		setFilterModel((prev) => ({
			...prev,
			page: page + 1 // DataGrid uses 0-based, API uses 1-based
		}));
	}, []);

	const onPageSizeChange = useCallback((pageSize: number) => {
		setFilterModel((prev) => ({
			...prev,
			pageSize,
			page: 1 // Reset to first page when changing page size
		}));
	}, []);

	const onFilterChange = useCallback((newFilterModel: GridFilterModel) => {
		setFilterModel((prev) => {
			// Rebuild from scratch so any previously-applied filter param is dropped
			const updated: PlaylistsControllerListEnrollmentsParams = {
				page: 1,
				pageSize: prev.pageSize,
				sortBy: prev.sortBy,
				sortDirection: prev.sortDirection
			};

			newFilterModel.items?.forEach((item: any) => {
				if (item.value !== undefined && item.value !== null && item.value !== "") {
					const field = item.columnField; // MUI DataGrid uses columnField, not field
					if (field && typeof field === "string") {
						const apiField = field as keyof PlaylistsControllerListEnrollmentsParams;
						updated[apiField] = item.value as never;
					}
				}
			});

			return updated;
		});
	}, []);

	const onSortModelChange = useCallback((sortModel: GridSortModel) => {
		setFilterModel((prev) => ({
			...prev,
			sortBy: sortModel.length > 0 ? sortModel[0].field : undefined,
			sortDirection:
				sortModel.length > 0
					? (sortModel[0].sort as "asc" | "desc")
					: undefined
		}));
	}, []);

	const sortModel = useMemo<GridSortModel>(
		() => [
			{
				field: filterModel.sortBy ?? "lastname",
				sort:
					filterModel.sortDirection === "desc"
						? "desc"
						: "asc"
			}
		],
		[filterModel.sortBy, filterModel.sortDirection]
	);

	const onRowSelectionChange = useCallback(
		(newSelection: number[]) => {
			setSelected(newSelection);
		},
		[setSelected]
	);

	// Default initial state for the DataGridPro
	const defaultInitialState: GridInitialState = {
		sorting: {
			sortModel: [{field: "lastname", sort: "asc"}] // Default sort
		},
		columns: {
			columnVisibilityModel: {
				email: false,
				supervisorName: false,
				hireDate: false,
				titleStartDate: false,
				customFieldValue1: false,
				customFieldValue2: false,
				customFieldValue3: false
			}
		}
	};

	return {
		enrollments,
		rowCount,
		isFetching,
		refetch,
		page: filterModel.page - 1, // DataGrid expects 0-based page
		pageSize: filterModel.pageSize,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		sortModel,
		onSortModelChange,
		onRowSelectionChange,
		defaultInitialState
	};
};
